"use client";

import { cva } from "class-variance-authority";
import type { ElementType, FC, HTMLAttributes, LiHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { Layout } from "@/types";
import {
  NotificationListContext,
  type NotificationPriority,
  useNotificationList,
} from "./notification-list.context";

export type NotificationListLayout = Extract<Layout, "desktop" | "mobile">;

export interface NotificationListProps extends HTMLAttributes<HTMLElement> {
  layout?: NotificationListLayout;
  as?: ElementType;
}

const Root: FC<NotificationListProps> = ({
  layout = "desktop",
  as: Tag = "ul",
  className,
  children,
  ...props
}) => (
  <NotificationListContext.Provider value={{ layout }}>
    <Tag
      data-layout={layout}
      className={cn(
        "divide-y divide-border-subtle overflow-hidden surface-card shadow-sm",
        className,
      )}
      {...props}
    >
      {children}
    </Tag>
  </NotificationListContext.Provider>
);

const PRIORITY_STYLES: Record<NotificationPriority, { stripe: string; icon: string }> = {
  high: { stripe: "border-l-feedback-danger", icon: "text-feedback-danger" },
  medium: { stripe: "border-l-feedback-warning", icon: "text-feedback-warning" },
  low: { stripe: "border-l-feedback-caution", icon: "text-feedback-caution" },
};

const BODY_VARIANTS = cva("min-w-0 flex-1", {
  variants: { layout: { desktop: "flex items-center gap-4", mobile: "" } },
});

const TRAILING_VARIANTS = cva("flex items-center gap-3", {
  variants: {
    layout: {
      desktop: "shrink-0",
      mobile: "mt-2 justify-between",
    },
  },
});

interface ItemProps extends Omit<LiHTMLAttributes<HTMLLIElement>, "title"> {
  icon: ReactNode;
  title: ReactNode;
  detail: ReactNode;
  time: string;
  priority?: NotificationPriority;
  /** Read by screen readers before the title, so color is not the only signal. */
  priorityLabel?: string;
  action?: ReactNode;
  /** When present, the title becomes a button that calls it. */
  onOpen?: () => void;
}

const Item: FC<ItemProps> = ({
  icon,
  title,
  detail,
  time,
  priority,
  priorityLabel,
  action,
  onOpen,
  className,
  ...props
}) => {
  const { layout } = useNotificationList();
  const styles = priority ? PRIORITY_STYLES[priority] : null;

  return (
    <li
      data-priority={priority ?? "none"}
      className={cn(
        "flex items-start gap-3 border-l-4 p-4",
        styles ? styles.stripe : "border-l-transparent",
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          "mt-0.5 h-5 w-5 shrink-0 [&>svg]:h-full [&>svg]:w-full",
          styles ? styles.icon : "text-fg-subtle",
        )}
      >
        {icon}
      </span>

      <div className={BODY_VARIANTS({ layout })}>
        <div className="min-w-0 flex-1">
          {priorityLabel && <span className="sr-only">{priorityLabel}: </span>}
          {onOpen ? (
            <button
              type="button"
              onClick={onOpen}
              className="rounded-sm text-left text-body-md font-semibold text-fg-strong hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {title}
            </button>
          ) : (
            <p className="text-body-md font-semibold text-fg-strong">{title}</p>
          )}
          <p className="mt-0.5 text-body-sm text-fg-muted">{detail}</p>
        </div>

        <div className={TRAILING_VARIANTS({ layout })}>
          <span className="text-body-xs text-fg-muted">{time}</span>
          {action}
        </div>
      </div>
    </li>
  );
};

Root.displayName = "NotificationList";
Item.displayName = "NotificationList.Item";

export const NotificationList = Object.assign(Root, { Item });
